import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Calculator } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SegregationPage = ({ user, token }) => {
  const [hubs, setHubs] = useState([]);
  const [selectedHub, setSelectedHub] = useState(user?.hub_id || "");
  const [staffNames, setStaffNames] = useState([]);
  const [allMaterials, setAllMaterials] = useState([]);
  const [useDropdownStaff, setUseDropdownStaff] = useState(true);
  const [personName, setPersonName] = useState("");
  const [quantities, setQuantities] = useState({});
  const [segregationList, setSegregationList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchHubs();
    fetchSegregationList();
    fetchStaffNames();
    fetchMaterialTypes();
  }, []);

  const fetchStaffNames = async () => {
    try {
      const response = await axios.get(`${API}/staff-names`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaffNames(response.data);
    } catch (error) {
      console.error("Error fetching staff names:", error);
    }
  };

  const fetchMaterialTypes = async () => {
    try {
      const response = await axios.get(`${API}/material-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Also fetch segregation to get historical materials with their rates
      const segResponse = await axios.get(`${API}/segregation`, {
        headers: { Authorization: `Bearer ${token}` },
        params: user?.hub_id ? { hub_id: user.hub_id } : {}
      });
      
      const adminMaterials = response.data;
      const adminMaterialNames = adminMaterials.map(m => m.name);
      
      // Get unique historical materials not in admin list, with their rates
      const historicalMap = {};
      segResponse.data.forEach(entry => {
        if (entry.material_type && !adminMaterialNames.includes(entry.material_type)) {
          // Use the most recent rate for each material
          if (!historicalMap[entry.material_type]) {
            historicalMap[entry.material_type] = entry.rate || 0;
          }
        }
      });
      
      const uniqueHistorical = Object.entries(historicalMap).map(([name, rate]) => ({
        id: `historical-${name}`,
        name: name,
        rate: rate
      }));
      
      setAllMaterials([...adminMaterials, ...uniqueHistorical]);
    } catch (error) {
      console.error("Error fetching material types:", error);
    }
  };

  const fetchHubs = async () => {
    try {
      const response = await axios.get(`${API}/hubs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHubs(response.data);
      if (response.data.length > 0 && !user?.hub_id) {
        setSelectedHub(response.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching hubs:", error);
    }
  };

  const fetchSegregationList = async () => {
    try {
      const response = await axios.get(`${API}/segregation`, {
        headers: { Authorization: `Bearer ${token}` },
        params: user?.hub_id ? { hub_id: user.hub_id } : {}
      });
      setSegregationList(response.data);
    } catch (error) {
      console.error("Error fetching segregation list:", error);
    }
  };

  const updateQuantity = (materialName, value) => {
    setQuantities(prev => ({
      ...prev,
      [materialName]: value
    }));
  };

  const calculateItemTotal = (material) => {
    const qty = parseFloat(quantities[material.name] || 0);
    const rate = parseFloat(material.rate || 0);
    return (qty * rate).toFixed(2);
  };

  const calculateGrandTotal = () => {
    return allMaterials.reduce((sum, material) => {
      return sum + parseFloat(calculateItemTotal(material));
    }, 0).toFixed(2);
  };

  const getItemsWithQuantity = () => {
    return allMaterials.filter(material => {
      const qty = parseFloat(quantities[material.name] || 0);
      return qty > 0;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!personName) {
      toast.error("Please enter person name");
      return;
    }

    const itemsWithQty = getItemsWithQuantity();
    if (itemsWithQty.length === 0) {
      toast.error("Please enter quantity for at least one material");
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${API}/segregation/batch`,
        {
          hub_id: selectedHub,
          person_name: personName,
          items: itemsWithQty.map(material => ({
            material_type: material.name,
            quantity: parseFloat(quantities[material.name]),
            rate: parseFloat(material.rate || 0)
          }))
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Created ${itemsWithQty.length} segregation entries!`);
      setPersonName("");
      setQuantities({});
      fetchSegregationList();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create entries");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="segregation-page">
      <div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-heading tracking-tight">Segregation</h1>
        <p className="text-muted-foreground text-sm sm:text-base mt-1">Track segregated materials by person</p>
      </div>

      <Card className="shadow-lg border-border/50">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-lg sm:text-xl lg:text-2xl font-heading">New Entry</CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {!user?.hub_id && (
                <div className="space-y-2">
                  <Label htmlFor="hub" className="text-sm">Hub</Label>
                  <Select value={selectedHub} onValueChange={setSelectedHub}>
                    <SelectTrigger data-testid="segregation-hub-select">
                      <SelectValue placeholder="Select Hub" />
                    </SelectTrigger>
                    <SelectContent>
                      {hubs.map((hub) => (
                        <SelectItem key={hub.id} value={hub.id}>
                          {hub.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="person_name" className="text-sm">Person Name</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setUseDropdownStaff(!useDropdownStaff)}
                    className="text-xs h-6 px-2"
                  >
                    {useDropdownStaff ? "Manual" : "Select"}
                  </Button>
                </div>
                {useDropdownStaff && staffNames.length > 0 ? (
                  <Select
                    value={personName}
                    onValueChange={setPersonName}
                  >
                    <SelectTrigger data-testid="segregation-person-select">
                      <SelectValue placeholder="Select staff" />
                    </SelectTrigger>
                    <SelectContent>
                      {staffNames.map((staff) => (
                        <SelectItem key={staff.id} value={staff.name}>
                          {staff.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="person_name"
                    data-testid="segregation-person-input"
                    type="text"
                    placeholder="Enter name"
                    value={personName}
                    onChange={(e) => setPersonName(e.target.value)}
                    className="h-9 sm:h-10"
                  />
                )}
              </div>
            </div>

            {/* Fixed Materials List */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold">Waste Items (Enter Quantity)</Label>
              
              {allMaterials.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No materials configured. Ask admin to add materials.</p>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="text-xs font-semibold">Material</TableHead>
                        <TableHead className="text-xs font-semibold w-24">Rate (₹/kg)</TableHead>
                        <TableHead className="text-xs font-semibold w-28">Qty (kg)</TableHead>
                        <TableHead className="text-xs font-semibold w-24 text-right">Total (₹)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allMaterials.map((material, index) => (
                        <TableRow key={material.id || index}>
                          <TableCell className="text-sm font-medium">{material.name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {material.rate ? `₹${material.rate}` : '-'}
                          </TableCell>
                          <TableCell>
                            <Input
                              data-testid={`segregation-qty-${index}`}
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="0"
                              value={quantities[material.name] || ""}
                              onChange={(e) => updateQuantity(material.name, e.target.value)}
                              className="h-8 text-sm w-24"
                            />
                          </TableCell>
                          <TableCell className="text-sm font-semibold text-right text-primary">
                            ₹{calculateItemTotal(material)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>

            <Card className="bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    <span className="font-medium text-sm sm:text-base">Grand Total ({getItemsWithQuantity().length} items)</span>
                  </div>
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold font-heading text-primary" data-testid="segregation-total-amount">
                    ₹{calculateGrandTotal()}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button
              data-testid="segregation-submit-button"
              type="submit"
              className="w-full h-9 sm:h-10"
              disabled={loading || allMaterials.length === 0}
            >
              {loading ? "Saving..." : `Save ${getItemsWithQuantity().length} Entries`}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="shadow-lg border-border/50">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-lg sm:text-xl lg:text-2xl font-heading">Recent Entries</CardTitle>
        </CardHeader>
        <CardContent className="p-0 sm:p-4 lg:p-6 sm:pt-0">
          <div className="overflow-x-auto">
            <Table data-testid="segregation-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs sm:text-sm">Date</TableHead>
                  <TableHead className="text-xs sm:text-sm">Person</TableHead>
                  <TableHead className="text-xs sm:text-sm">Material</TableHead>
                  <TableHead className="text-xs sm:text-sm">Qty</TableHead>
                  <TableHead className="text-xs sm:text-sm">Rate</TableHead>
                  <TableHead className="text-xs sm:text-sm">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {segregationList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground text-sm">
                      No entries yet
                    </TableCell>
                  </TableRow>
                ) : (
                  segregationList.slice(0, 15).map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="text-xs sm:text-sm">{entry.date}</TableCell>
                      <TableCell className="text-xs sm:text-sm">{entry.person_name}</TableCell>
                      <TableCell className="text-xs sm:text-sm">{entry.material_type}</TableCell>
                      <TableCell className="text-xs sm:text-sm">{entry.quantity}</TableCell>
                      <TableCell className="text-xs sm:text-sm">₹{entry.rate}</TableCell>
                      <TableCell className="text-xs sm:text-sm font-semibold">₹{entry.total_amount}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SegregationPage;
